import React from 'react';
import { useHistory } from 'react-router-dom';
import { type LaunchDarklyFlags } from '@/common/launchDarkly';
import { useIsSkySlopeMobileApp } from '@/common/utils';
import { BlockShelfDrawer } from '@/components/senderBuild/block-shelf-drawer';
// eslint-disable-next-line import/no-named-as-default
import Toolbar from '@/components/senderBuild/Toolbar';
import { EnvelopeShelf } from '../../components/senderBuild/EnvelopeShelf';
import { InfoBoxDialog } from '../../components/senderBuild/InfoBoxDialog';
import MobileRecipientsOrder from '../../components/senderBuild/MobileRecipientsOrder';
import { PdfViewer } from '../../components/senderBuild/PdfViewer';
import SavingOverlay from '../../components/senderBuild/SavingOverlay';
import { useIsMediumScreenMediaQuery, useIsSmallScreenMediaQuery } from '../../lib/isSmallScreen';
import { useShallowSelector } from '../../lib/reduxHooks';
import { MobileAppEditEmailModal } from './mobile-app-edit-email-modal';
import { useBackspaceKeyListener } from './use-backspace-key-listener';
import { useEnableTextBlockEditingKeyListener } from './use-enable-text-block-editing-key-listener';
import { usePasteBlockKeyListener } from './use-paste-block-key-listener';
import { useSetDefaultZoom } from './use-set-default-zoom';
import { useSetEnvBuilderHeaderContent } from './use-set-env-builder-header-content';

export function BuilderLayout(props: {
  flags: LaunchDarklyFlags;
  footer: React.ReactElement;
  children?: React.ReactNode;
}) {
  const { flags, footer, children } = props;

  const state = useShallowSelector((state) => ({
    templateId: state.senderBuild.id ?? '',
    config: state.senderBuild.config,
    done: state.senderBuild.done,
    recipientsFetched: state.senderBuild.recipientsFetched,
    saving: state.senderBuild.saving,
    settings: state.pageFrame.settings,
    status: state.senderBuild.status,
    fetchError: state.senderBuild.fetchError,
    isLoading: state.pageFrame.loading,
    settingsFetched: state.pageFrame.settingsFetched,
    mobileSigningOrderOpen: state.senderBuild.mobileSigningOrderOpen,
    recipientsModal: state.senderBuild.recipientsModal,
  }));

  const isMdScreenOrSmaller = useIsMediumScreenMediaQuery();
  const isSmScreenOrSmaller = useIsSmallScreenMediaQuery();
  const history = useHistory();

  const [isInfoBoxDialogOpen, setIsInfoBoxDialogOpen] = React.useState(true);
  const isMobileApp = useIsSkySlopeMobileApp();

  const setDefaultZoom = useSetDefaultZoom({ isSmallScreen: isSmScreenOrSmaller, isFullScreen: isMdScreenOrSmaller });
  React.useEffect(setDefaultZoom, [setDefaultZoom]);

  useSetEnvBuilderHeaderContent({ flags });

  useBackspaceKeyListener();
  useEnableTextBlockEditingKeyListener();
  usePasteBlockKeyListener();

  if (state.fetchError) {
    history.push('/');
    return null;
  }
  if (state.isLoading || !state.settingsFetched) return null;

  return (
    <div className="w-full">
      <Toolbar />

      {isMdScreenOrSmaller ? null : (
        <>
          <EnvelopeShelf />
          <BlockShelfDrawer />
        </>
      )}

      <PdfViewer />
      {state.saving && <SavingOverlay />}

      {children}

      {state.saving && isMobileApp ? null : footer}

      {state.mobileSigningOrderOpen && (
        <MobileRecipientsOrder recipients={state.recipientsModal} id={state.templateId} />
      )}
      <MobileAppEditEmailModal />
      {state.config?.senderUi?.infoBox && (
        <InfoBoxDialog
          isOpen={isInfoBoxDialogOpen}
          onClose={() => setIsInfoBoxDialogOpen(false)}
          infoBox={state.config?.senderUi?.infoBox}
        />
      )}
    </div>
  );
}
